import { useState } from 'react'
import { Columns3, Sparkles, Loader2 } from 'lucide-react'
import { ColumnVisibilityToggle } from '../ColumnVisibilityToggle'
import type { GroupBy } from '../CommandBar'

interface Props {
  groupBy: GroupBy
  onGroupByChange: (g: GroupBy) => void
  visibleColumns: Record<string, boolean>
  onVisibleColumnsChange: (cols: Record<string, boolean>) => void
  /** AI generate handler. When omitted, the button hides. */
  onGenerate?: () => void
  isGenerating?: boolean
  newlyDetected?: number
}

const GROUPS: Array<{ key: GroupBy; label: string }> = [
  { key: 'hierarchy', label: 'Hierarchy' },
  { key: 'status',    label: 'Status'    },
  { key: 'priority',  label: 'Priority'  },
  { key: 'assignee',  label: 'Assignee'  },
]

/**
 * Table-only trailing controls slotted into ViewToolbar: Group segment,
 * Columns picker, and Generate pill.
 *
 * Search + Filter + chips live in the shared ViewToolbar — this is everything
 * that's specific to the spreadsheet view.
 */
export function TableTrailing({
  groupBy, onGroupByChange,
  visibleColumns, onVisibleColumnsChange,
  onGenerate, isGenerating, newlyDetected,
}: Props) {
  const [showColumnPicker, setShowColumnPicker] = useState(false)

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Group segmented control */}
      <div
        className="inline-flex p-1 rounded-lg"
        style={{ background: 'var(--color-warm-100)', border: '1px solid var(--color-warm-200)' }}
        role="tablist"
        aria-label="Group by"
      >
        {GROUPS.map(g => {
          const active = groupBy === g.key
          return (
            <button
              key={g.key}
              role="tab"
              aria-selected={active}
              onClick={() => onGroupByChange(g.key)}
              className="rounded-md font-medium border-0 cursor-pointer transition-colors px-2.5 py-1 text-sm"
              style={{
                background: active ? 'var(--color-bg-card)' : 'transparent',
                color: active ? 'var(--color-fg)' : 'var(--color-fg-secondary)',
                fontWeight: active ? 600 : 500,
                boxShadow: active ? '0 1px 2px rgba(29,26,21,0.06)' : undefined,
              }}
            >
              {g.label}
            </button>
          )
        })}
      </div>

      <div className="relative">
        <GhostButton
          icon={<Columns3 className="h-4 w-4" />}
          onClick={() => setShowColumnPicker(v => !v)}
        >
          Columns
        </GhostButton>
        {showColumnPicker && (
          <ColumnVisibilityToggle
            visibleColumns={visibleColumns}
            onToggle={(col) => onVisibleColumnsChange({ ...visibleColumns, [col]: !visibleColumns[col] })}
            onClose={() => setShowColumnPicker(false)}
          />
        )}
      </div>

      {onGenerate && (
        <button
          type="button"
          onClick={onGenerate}
          disabled={isGenerating}
          className="inline-flex items-center gap-1.5 rounded-lg cursor-pointer font-medium border-0 px-3 py-1.5 text-sm transition-colors"
          style={{
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-warm-200)',
            color: 'var(--color-fg)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-bg-card)')}
          title={newlyDetected
            ? `Skerpa found ${newlyDetected} new requirement${newlyDetected === 1 ? '' : 's'}`
            : 'Generate requirements from research'}
        >
          {isGenerating
            ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--color-solution)' }} />
            : <Sparkles className="h-4 w-4" style={{ color: 'var(--color-solution)' }} />
          }
          Generate
          {newlyDetected !== undefined && newlyDetected > 0 && (
            <span
              className="font-mono tabular-nums inline-flex items-center justify-center"
              style={{
                fontSize: 11, fontWeight: 600,
                padding: '0 6px', minWidth: 18, height: 18,
                borderRadius: 999,
                background: 'var(--color-solution)', color: 'var(--color-fg)',
              }}
            >
              {newlyDetected}
            </span>
          )}
        </button>
      )}

    </div>
  )
}

function GhostButton({ icon, onClick, children }: {
  icon: React.ReactNode; onClick?: () => void; children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg cursor-pointer font-medium border-0 px-3 py-1.5 text-sm transition-colors"
      style={{
        background: 'transparent',
        color: 'var(--color-fg-secondary)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {icon}
      {children}
    </button>
  )
}
